import {
  IconButton,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  TableBody,
  Paper,
} from "@mui/material";
import IMAGES from "../../../../../assets/images/images";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useEffect, useState } from "react";
import { AreaType } from "../../../../../domain/models/executive-summary/executive-summary-type";
import ExecutiveSummaryDetail from "./ExecutiveSummaryDetail";
import { DownloadJSONExecutive } from "../../../../../domain/models/executive-summary/download-json-executive";

type Props = {
  data: AreaType[];
  downloadJSON: DownloadJSONExecutive[];
};

const ExecutiveSummaryTable: React.FC<Props> = ({ data, downloadJSON }) => {
  const [channelPaisaColumn, setChannelPaisaColumn] = useState<boolean>(false);
  const [channelPayColumn, setChannelPayColumn] = useState<boolean>(false);
  const currentMonth = new Date();
  const currentMonthIndex = currentMonth.getMonth() + 1;
  const currentYear = currentMonth.getFullYear();
  const [month, setMonth] = useState<number>(
    currentMonth.getMonth() === 0 ? 12 : currentMonth.getMonth()
  );
  const [opened, setOpened] = useState<boolean[]>(
    new Array<boolean>(data.length).fill(false)
  );
  const [year, setYear] = useState<number>(
    currentMonth.getMonth() === 0
      ? currentMonth.getFullYear() - 1
      : currentMonth.getFullYear()
  );

  const getMonthName = (monthNumber: number) => {
    const date = new Date();
    date.setFullYear(2000, monthNumber - 1, 1);
    return date.toLocaleString("default", { month: "long" }).toUpperCase();
  };

  const expandRow = (name: string) => {
    if (name === "channelPaisa") {
      setChannelPaisaColumn(!channelPaisaColumn);
      setChannelPayColumn(false);
    } else if (name === "channelPay") {
      setChannelPayColumn(!channelPayColumn);
      setChannelPaisaColumn(false);
    }

    setMonth(currentMonth.getMonth() === 0 ? 12 : currentMonth.getMonth());
    setYear(
      currentMonth.getMonth() === 0
        ? currentMonth.getFullYear() - 1
        : currentMonth.getFullYear()
    );
  };

  const handleNextMonth = () => {
    if (month === 12) {
      setYear(year + 1);
      setMonth(1);
    } else {
      setMonth(month + 1);
    }
  };

  const handlePrevMonth = () => {
    if (month === 1) {
      setYear(year - 1);
      setMonth(12);
    } else {
      setMonth(month - 1);
    }
  };

  const earliestMonthIndex = (currentMonthIndex - 6 + 12) % 12 || 12; // Wrap around to keep within 1-12 range
  const earliestYear = currentMonthIndex <= 6 ? currentYear - 1 : currentYear;
  const latestMonthIndex = currentMonthIndex - 1 || 12;
  const latestYear = currentMonthIndex === 1 ? currentYear - 1 : currentYear;

  const isPrevDisabled = year === earliestYear && month <= earliestMonthIndex;
  const isNextDisabled = year === latestYear && month >= latestMonthIndex;

  return (
    <TableContainer component={Paper}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              padding="none"
              sx={{
                border: "none",
                bgcolor: "#E9EDF5",
              }}
              rowSpan={channelPaisaColumn || channelPayColumn ? 2 : 1}
            />
            <TableCell
              padding="none"
              rowSpan={channelPaisaColumn || channelPayColumn ? 2 : 1}
              sx={{
                border: "none",
                bgcolor: "#E9EDF5",
                width: opened.find((a) => a === true) ? "10vw" : "25vw",
                color: "#687182",
              }}
            >
              AREA
            </TableCell>
            {opened.find((a) => a === true) && (
              <TableCell
                padding="none"
                rowSpan={channelPaisaColumn || channelPayColumn ? 2 : 1}
                sx={{
                  border: "none",
                  bgcolor: "#E9EDF5",
                  width: "15vw",
                  color: "#687182",
                }}
              >
                HEADQUARTER
              </TableCell>
            )}
            <TableCell
              padding="none"
              rowSpan={channelPaisaColumn || channelPayColumn ? 2 : 1}
              sx={{
                border: "none",
                textAlign: "center",
                bgcolor: "#E9EDF5",
                width: "5vw",
                color: "#687182",
              }}
            >
              <img src={IMAGES.ChannelitixIcon} alt="Channelitix" />
            </TableCell>
            <TableCell
              padding="none"
              rowSpan={channelPaisaColumn || channelPayColumn ? 2 : 1}
              sx={{
                border: "none",
                textAlign: "center",
                bgcolor: "#E9EDF5",
                width: "5vw",
                color: "#687182",
              }}
            >
              TARGET
            </TableCell>
            <TableCell
              padding="none"
              rowSpan={channelPaisaColumn || channelPayColumn ? 2 : 1}
              sx={{
                border: "none",
                textAlign: "center",
                bgcolor: "#E9EDF5",
                width: "5vw",
                color: "#687182",
              }}
            >
              SALES
            </TableCell>
            <TableCell
              padding="none"
              rowSpan={channelPaisaColumn || channelPayColumn ? 2 : 1}
              sx={{
                border: "none",
                textAlign: "center",
                bgcolor: "#E9EDF5",
                width: "5vw",
                color: "#687182",
              }}
            >
              %
            </TableCell>
            <TableCell
              padding="none"
              rowSpan={channelPaisaColumn || channelPayColumn ? 2 : 1}
              sx={{
                border: "none",
                textAlign: "center",
                bgcolor: "#E9EDF5",
                width: "5vw",
                color: "#687182",
              }}
            >
              GROWTH
            </TableCell>
            {channelPaisaColumn && (
              <>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#c6c7e5",
                    width: 10,
                  }}
                  rowSpan={2}
                >
                  <Stack>
                    <img src={IMAGES.ChannelPay} alt="Channel Pay" />
                    <IconButton
                      size="small"
                      onClick={() => expandRow("channelPay")}
                    >
                      {!channelPayColumn ? (
                        <NavigateNextIcon
                          fontSize="small"
                          sx={{
                            bgcolor: "white",
                            borderRadius: "50%",
                            color: "#000088",
                          }}
                        />
                      ) : (
                        <NavigateBeforeIcon
                          fontSize="small"
                          sx={{
                            bgcolor: "white",
                            borderRadius: "50%",
                            color: "#000088",
                          }}
                        />
                      )}
                    </IconButton>
                  </Stack>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f4e5cf",
                    width: 10,
                    padding: 0,
                  }}
                  rowSpan={2}
                >
                  <img src={IMAGES.ChannelPaisa} alt="Channel Paisa" />
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f9f2e7",
                    color: "#687182",
                  }}
                  colSpan={3}
                >
                  <IconButton
                    onClick={handlePrevMonth}
                    disabled={isPrevDisabled}
                  >
                    <NavigateBeforeIcon
                      fontSize="small"
                      sx={{
                        bgcolor: "white",
                        borderRadius: "50%",
                        color: isPrevDisabled ? "gray" : "#E68900",
                      }}
                    />
                  </IconButton>
                  {getMonthName(month)} {year}
                  <IconButton
                    onClick={handleNextMonth}
                    disabled={isNextDisabled}
                  >
                    <NavigateNextIcon
                      fontSize="small"
                      sx={{
                        bgcolor: "white",
                        borderRadius: "50%",
                        color: isNextDisabled ? "gray" : "#E68900",
                      }}
                    />
                  </IconButton>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f7eddd",
                    color: "#687182",
                  }}
                  colSpan={3}
                >
                  {getMonthName(currentMonth.getMonth() + 1)}{" "}
                  {currentMonth.getFullYear()}
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f7eddd",
                    width: 10,
                  }}
                  rowSpan={2}
                >
                  <IconButton
                    size="small"
                    onClick={() => expandRow("channelPaisa")}
                  >
                    {!channelPaisaColumn ? (
                      <NavigateNextIcon
                        fontSize="small"
                        sx={{
                          bgcolor: "white",
                          borderRadius: "50%",
                          color: "#E68900",
                        }}
                      />
                    ) : (
                      <NavigateBeforeIcon
                        fontSize="small"
                        sx={{
                          bgcolor: "white",
                          borderRadius: "50%",
                          color: "#E68900",
                        }}
                      />
                    )}
                  </IconButton>
                </TableCell>
              </>
            )}
            {channelPayColumn && (
              <>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#c6c7e5",
                  }}
                  rowSpan={2}
                >
                  <img src={IMAGES.ChannelPay} alt="Channel Pay" />
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#b8bad9",
                    color: "white",
                  }}
                  colSpan={3}
                >
                  <IconButton
                    onClick={handlePrevMonth}
                    disabled={isPrevDisabled}
                  >
                    <NavigateBeforeIcon
                      color="inherit"
                      fontSize="small"
                      sx={{
                        bgcolor: "white",
                        borderRadius: "50%",
                        color: isPrevDisabled ? "gray" : "#000088",
                      }}
                    />
                  </IconButton>
                  {getMonthName(month)} {year}
                  <IconButton
                    onClick={handleNextMonth}
                    disabled={isNextDisabled}
                  >
                    <NavigateNextIcon
                      fontSize="small"
                      sx={{
                        bgcolor: "white",
                        borderRadius: "50%",
                        color: isNextDisabled ? "gray" : "#000088",
                      }}
                    />
                  </IconButton>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#c6c7e5",
                    color: "white",
                  }}
                  colSpan={3}
                >
                  {getMonthName(currentMonth.getMonth() + 1)}{" "}
                  {currentMonth.getFullYear()}
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#c6c7e5",
                    width: 10,
                  }}
                  rowSpan={2}
                >
                  <IconButton
                    size="small"
                    onClick={() => expandRow("channelPay")}
                  >
                    {!channelPayColumn ? (
                      <NavigateNextIcon
                        fontSize="small"
                        sx={{
                          bgcolor: "white",
                          borderRadius: "50%",
                          color: "#000088",
                        }}
                      />
                    ) : (
                      <NavigateBeforeIcon
                        fontSize="small"
                        sx={{
                          bgcolor: "white",
                          borderRadius: "50%",
                          color: "#000088",
                        }}
                      />
                    )}
                  </IconButton>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f4e5cf",
                    width: 10,
                  }}
                  rowSpan={2}
                >
                  <Stack>
                    <img src={IMAGES.ChannelPaisa} alt="Channel Paisa" />
                    <IconButton
                      size="small"
                      onClick={() => expandRow("channelPaisa")}
                    >
                      {!channelPaisaColumn ? (
                        <NavigateNextIcon
                          fontSize="small"
                          sx={{
                            bgcolor: "white",
                            borderRadius: "50%",
                            color: "#E68900",
                          }}
                        />
                      ) : (
                        <NavigateBeforeIcon
                          fontSize="small"
                          sx={{
                            bgcolor: "white",
                            borderRadius: "50%",
                            color: "#E68900",
                          }}
                        />
                      )}
                    </IconButton>
                  </Stack>
                </TableCell>
              </>
            )}
            {!channelPaisaColumn && !channelPayColumn && (
              <>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#c6c7e5",
                    padding: 0,
                  }}
                >
                  <img src={IMAGES.ChannelPay} alt="Channel Pay" />
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#c6c7e5",
                    color: "white",
                  }}
                >
                  INVOICES
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#c6c7e5",
                    color: "white",
                  }}
                >
                  ORDER
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#c6c7e5",
                    color: "white",
                  }}
                >
                  ACTIVE
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#c6c7e5",
                    width: 10,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => expandRow("channelPay")}
                  >
                    {!channelPayColumn ? (
                      <NavigateNextIcon
                        fontSize="small"
                        sx={{
                          bgcolor: "white",
                          borderRadius: "50%",
                          color: "#000088",
                        }}
                      />
                    ) : (
                      <NavigateBeforeIcon
                        fontSize="small"
                        sx={{
                          bgcolor: "white",
                          borderRadius: "50%",
                          color: "#000088",
                        }}
                      />
                    )}
                  </IconButton>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f4e5cf",
                    padding: 0,
                  }}
                >
                  <img src={IMAGES.ChannelPaisa} alt="Channel Paisa" />
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f4e5cf",
                    color: "#687182",
                  }}
                >
                  INVOICES
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f4e5cf",
                    color: "#687182",
                  }}
                >
                  ORDER
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f4e5cf",
                    color: "#687182",
                  }}
                >
                  ACTIVE
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    border: "none",
                    textAlign: "center",
                    bgcolor: "#f4e5cf",
                    width: 10,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => expandRow("channelPaisa")}
                  >
                    {!channelPaisaColumn ? (
                      <NavigateNextIcon
                        fontSize="small"
                        sx={{
                          bgcolor: "white",
                          borderRadius: "50%",
                          color: "#E68900",
                        }}
                      />
                    ) : (
                      <NavigateBeforeIcon
                        fontSize="small"
                        sx={{
                          bgcolor: "white",
                          borderRadius: "50%",
                          color: "#E68900",
                        }}
                      />
                    )}
                  </IconButton>
                </TableCell>
              </>
            )}
          </TableRow>
          {(channelPaisaColumn || channelPayColumn) && (
            <TableRow>
              {channelPaisaColumn && (
                <>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#f9f2e7",
                      color: "#687182",
                    }}
                  >
                    INVOICES
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#f9f2e7",
                      color: "#687182",
                    }}
                  >
                    ORDER
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#f9f2e7",
                      color: "#687182",
                    }}
                  >
                    ACTIVE
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#f7eddd",
                      color: "#687182",
                    }}
                  >
                    INVOICES
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#f7eddd",
                      color: "#687182",
                    }}
                  >
                    ORDER
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#f7eddd",
                      color: "#687182",
                    }}
                  >
                    ACTIVE
                  </TableCell>
                </>
              )}
              {channelPayColumn && (
                <>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#b8bad9",
                      color: "white",
                    }}
                  >
                    INVOICES
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#b8bad9",
                      color: "white",
                    }}
                  >
                    ORDER
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#b8bad9",
                      color: "white",
                    }}
                  >
                    ACTIVE
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#c6c7e5",
                      color: "white",
                    }}
                  >
                    INVOICES
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#c6c7e5",
                      color: "white",
                    }}
                  >
                    ORDER
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{
                      border: "none",
                      textAlign: "center",
                      bgcolor: "#c6c7e5",
                      color: "white",
                    }}
                  >
                    ACTIVE
                  </TableCell>
                </>
              )}
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <>
              <ExecutiveSummaryDetail
                data={row}
                channelPaisaColumn={channelPaisaColumn}
                channelPayColumn={channelPayColumn}
                month={month}
                year={year}
                setOpened={setOpened}
                index={index}
                opened={opened}
                downloadJSON={downloadJSON}
              />
            </>
          ))}
          <TableRow></TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExecutiveSummaryTable;
